import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../../services/common/auth.service';
import { LawpayPaymentService } from '../../../services/lawpay/lawpay-payment.service';

@Component({
  selector: 'app-lawpay-processed-payments',
  templateUrl: './lawpay-processed-payments.component.html',
  styleUrls: ['./lawpay-processed-payments.component.css']
})
export class LawpayProcessedPaymentsComponent implements OnInit {
  payments: any[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  totalPages: number = 1;
  perPage: number = 25;

  searchParams: any = {
    status: 'processed',
    per_page: 25,
    page: 1
  };

  selectedPayment: any = null;
  refundReason: string = '';
  refundAmount: number = 0;
  refundPreview: any = null;
  loadingPreview: boolean = false;

  loading: boolean = false;
  refunding: boolean = false;
  showRefundModal: boolean = false;

  constructor(
    private authService: AuthService,
    private lawpayPaymentService: LawpayPaymentService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.search();
  }

  search(): void {
    this.loading = true;
    this.lawpayPaymentService.index(this.searchParams).then(result => {
      this.payments = result.payments;
      this.totalCount = result.total_count;
      this.currentPage = result.current_page;
      this.totalPages = result.total_pages;
      this.perPage = result.per_page;
      this.loading = false;
    }).catch(error => {
      console.error('Error loading payments:', error);
      this.loading = false;
    });
  }

  pageChanged(event: any): void {
    this.searchParams.page = event.page;
    this.search();
  }

  openRefundModal(payment: any): void {
    this.selectedPayment = payment;
    this.refundAmount = payment.amount;
    this.refundReason = '';
    this.refundPreview = null;
    this.showRefundModal = true;
    this.loadRefundPreview();
  }

  closeRefundModal(): void {
    this.showRefundModal = false;
    this.selectedPayment = null;
    this.refundAmount = 0;
    this.refundReason = '';
    this.refundPreview = null;
  }

  loadRefundPreview(): void {
    if (!this.selectedPayment) return;
    if (this.refundAmount <= 0 || this.refundAmount > this.selectedPayment.amount) {
      this.refundPreview = null;
      return;
    }
    this.loadingPreview = true;
    this.lawpayPaymentService.refundPreview(this.selectedPayment.id, this.refundAmount)
      .then(result => {
        this.refundPreview = result;
        this.loadingPreview = false;
      })
      .catch(error => {
        console.error('Error loading refund preview:', error);
        this.refundPreview = null;
        this.loadingPreview = false;
      });
  }

  confirmRefund(): void {
    if (!this.selectedPayment) return;

    if (this.refundAmount <= 0 || this.refundAmount > this.selectedPayment.amount) {
      alert('Invalid refund amount');
      return;
    }

    this.refunding = true;
    this.lawpayPaymentService.refund(
      this.selectedPayment.id,
      this.refundAmount,
      this.refundReason || 'Refund requested'
    ).then(result => {
      this.refunding = false;
      if (result.success) {
        alert(result.message || 'Refund submitted to LawPay. V7 will record the bookkeeping when the LawPay callback arrives.');
        this.closeRefundModal();
        this.search(); // Refresh list
      } else {
        alert(result.error || 'Refund failed');
      }
    }).catch(error => {
      this.refunding = false;
      console.error('Error processing refund:', error);
      alert(this.refundErrorMessage(error));
    });
  }

  refundErrorMessage(error: any): string {
    const body = error && error.error ? error.error : error;
    if (body && body.error) return `Refund failed: ${body.error}`;
    if (body && body.message) return `Refund failed: ${body.message}`;
    if (error && error.message) return `Refund failed: ${error.message}`;
    return 'Refund failed. Please try again.';
  }

  canRefund(payment: any): boolean {
    if (payment.is_refunded) return false;
    if (payment.refund_pending) return false;

    // Check 6-month limit
    const createdAt = new Date(payment.created_at);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return createdAt > sixMonthsAgo;
  }

  isRefundOrReversal(payment: any): boolean {
    if (!payment) return false;
    return !!(payment.refund_or_reversal || payment.is_reversal || payment.is_refunded);
  }

  viewReceipt(payment: any): void {
    if (payment.receipt_id) {
      window.location.href = `/receipts/${payment.receipt_id}`;
    }
  }

  viewEntryGroup(payment: any): void {
    if (payment.entry_group_url) {
      window.location.href = payment.entry_group_url;
    } else if (payment.entry_group_id) {
      window.location.href = `/entry_groups/${payment.entry_group_id}`;
    }
  }

  refresh(): void {
    this.search();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getStatusBadgeClass(payment: any): string {
    if (payment.is_refunded) {
      return 'badge-danger';
    }
    if (payment.refund_failed) {
      return 'badge-warning';
    }
    if (payment.refund_pending) {
      return 'badge-info';
    }
    return 'badge-success';
  }

  getStatusText(payment: any): string {
    if (payment.is_refunded) {
      return 'Refunded';
    }
    if (payment.refund_failed) {
      return 'Refund Failed';
    }
    if (payment.refund_pending) {
      return 'Refund Pending';
    }
    return 'Processed';
  }
}
